import { isDefined } from "@ls/js-utils";
import SummaryType from "state/selections/Constants/SummaryType";
import PivotBetaDefinition from "./PivotBetaDefinition";
import PivotFADefinition from "./PivotFADefinition";
import PivotFilterDefinition from "./PivotFilterDefinition";

export default class PivotDefinition {
  static getDefault = () =>
    new PivotDefinition({
      levels: "MSAA",
      analytics: "weight",
      summary: SummaryType.ABSOLUTE,
      pruneBuckets: false,
      lookthru: false,
      filterDefinitions: [],
      betaDefinitions: [],
      faDefinitions: [],
      workspaceTradeType: "DEFAULT",
      scaleType: "Contribution",
    });

  static fromObject = (object: any) => {
    const {
      levels,
      analytics,
      summary,
      pruneBuckets,
      lookthru,
      filterDefinitions,
      betaDefinitions,
      betaDefintions, //betaDefintions is a typo that was used in the past. keep it here for backwards compatibility
      faDefinitions,
      workspaceTradeType,
      scaleType,
    } = object;
    return new PivotDefinition({
      levels,
      analytics,
      summary,
      pruneBuckets,
      lookthru,
      filterDefinitions: filterDefinitions?.map(
        (def) => new PivotFilterDefinition(def),
      ),
      betaDefinitions: (betaDefintions || betaDefinitions)?.map(
        //betaDefintions should only be populated if project was saved before the typo was fixed
        (def) => new PivotBetaDefinition(def),
      ),
      faDefinitions: faDefinitions?.map((def) => new PivotFADefinition(def)),
      workspaceTradeType: workspaceTradeType ?? "DEFAULT", //workspaceTradeType is added later and may not exist on existing grid states. default to "DEFAULT" to keep backwards compatibility
      scaleType: scaleType ?? "Contribution",
    });
  };

  readonly levels: string;
  readonly analytics: string;
  readonly summary: SummaryType;
  readonly pruneBuckets: boolean;
  readonly lookthru: boolean;
  readonly filterDefinitions: any[];
  readonly betaDefinitions: any[];
  readonly faDefinitions: any[];
  readonly enableRootNormalization: boolean;
  readonly key: string;
  readonly complete: boolean;
  readonly workspaceTradeType: "BUY" | "SELL" | "DEFAULT";
  readonly scaleType: string;

  constructor(props: {
    levels: PivotDefinition["levels"];
    analytics: PivotDefinition["analytics"];
    summary: PivotDefinition["summary"];
    pruneBuckets: PivotDefinition["pruneBuckets"];
    lookthru: PivotDefinition["lookthru"];
    filterDefinitions: PivotDefinition["filterDefinitions"];
    betaDefinitions: PivotDefinition["betaDefinitions"];
    faDefinitions: PivotDefinition["faDefinitions"];
    workspaceTradeType: PivotDefinition["workspaceTradeType"];
    scaleType: PivotDefinition["scaleType"];
  }) {
    const {
      levels,
      analytics,
      summary,
      pruneBuckets,
      lookthru,
      filterDefinitions,
      betaDefinitions,
      faDefinitions,
      workspaceTradeType,
      scaleType,
    } = props;

    this.levels = levels;
    this.analytics = analytics;
    this.summary = summary;
    this.pruneBuckets = pruneBuckets ?? false;
    this.lookthru = lookthru ?? true;
    this.filterDefinitions =
      filterDefinitions?.map((def) => new PivotFilterDefinition(def)) ?? [];
    this.betaDefinitions =
      betaDefinitions?.map((def) => new PivotBetaDefinition(def)) ?? [];
    this.faDefinitions =
      faDefinitions?.map((def) => new PivotFADefinition(def)) ?? [];
    this.enableRootNormalization = false;
    this.workspaceTradeType = workspaceTradeType;
    this.scaleType = scaleType ?? "Contribution";
    this.key = [
      this.levels,
      this.analytics,
      this.summary,
      this.pruneBuckets,
      this.workspaceTradeType,
      this.scaleType,
      ...(this.lookthru ? [] : [false]), //lookthru=true is blank to ensure backwards compatibility
      this.filterDefinitions.map(({ key }) => key).join(","),
      this.betaDefinitions.map(({ key }) => key).join(","),
      this.faDefinitions.map(({ key }) => key).join(","),
    ].join(",");
    this.complete = isDefined(this.levels) && isDefined(this.analytics);
  }

  setLevels = (value) =>
    new PivotDefinition({
      ...this,
      levels: value,
    });
  setAnalytics = (value) =>
    new PivotDefinition({
      ...this,
      analytics: value,
    });
  setSummary = (value) =>
    new PivotDefinition({
      ...this,
      summary: value,
    });
  setPruneBuckets = (value) =>
    new PivotDefinition({
      ...this,
      pruneBuckets: value,
    });
  setLookthru = (value) =>
    new PivotDefinition({
      ...this,
      lookthru: value,
    });
  setFilterDefinitions = (value) =>
    new PivotDefinition({
      ...this,
      filterDefinitions: value,
    });
  setBetaDefinitions = (value) =>
    new PivotDefinition({
      ...this,
      betaDefinitions: value,
    });
  setFaDefinitions = (value) =>
    new PivotDefinition({
      ...this,
      faDefinitions: value,
    });

  setWorkspaceTradeType = (value) =>
    new PivotDefinition({
      ...this,
      workspaceTradeType: value,
    });

  setScaleType = (value) =>
    new PivotDefinition({
      ...this,
      scaleType: value,
      summary: value === "RATIO" ? SummaryType.RELATIVE : this.summary,
    });

  copy = () => {
    return {
      ...this,
      filterDefinitions: this.filterDefinitions.map((d) => d.copy()),
      betaDefinitions: this.betaDefinitions.map((d) => d.copy()),
      faDefinitions: this.faDefinitions.map((d) => d.copy()),
    };
  };
}
